"use client";

import { useEffect, useRef, useState } from "react";
import {
	isProSealAvailable,
	PRO_SEAL_PROFILE_URL,
	PRO_SEAL_SCRIPT_URL,
	PRO_SEAL_WIDGET_ID,
	resolveProSealLanguage,
} from "@/lib/provenexpert/pro-seal";

const SCRIPT_ID = "proven-expert-pro-seal-runtime";
const AVAILABILITY_TIMEOUT_MS = 4_000;
const SCRIPT_TIMEOUT_MS = 8_000;

type ProSealStatus = "fallback" | "loading" | "ready";

interface ProSealApi {
	proSeal(config: {
		widgetId: string;
		language: string;
		usePageLanguage: boolean;
		bannerColor: string;
		textColor: string;
		showBackPage: boolean;
		showReviews: boolean;
		hideDate: boolean;
		hideName: boolean;
		googleStars: boolean;
		displayReviewerLastName: boolean;
		embeddedSelector: string;
	}): void;
}

declare global {
	interface Window {
		provenExpert?: ProSealApi;
	}
}

let scriptPromise: Promise<ProSealApi> | null = null;

function getProSealApi(): ProSealApi | null {
	return typeof window.provenExpert?.proSeal === "function" ? window.provenExpert : null;
}

function loadProSealScript(): Promise<ProSealApi> {
	const loadedApi = getProSealApi();
	if (loadedApi) return Promise.resolve(loadedApi);
	if (scriptPromise) return scriptPromise;

	scriptPromise = new Promise<ProSealApi>((resolve, reject) => {
		const existing = document.getElementById(SCRIPT_ID);
		const script = existing instanceof HTMLScriptElement ? existing : document.createElement("script");
		let settled = false;

		const cleanup = () => {
			window.clearTimeout(timeoutId);
			script.removeEventListener("load", handleLoad);
			script.removeEventListener("error", handleError);
		};
		const fail = () => {
			if (settled) return;
			settled = true;
			cleanup();
			script.remove();
			reject(new Error("ProvenExpert ProSeal runtime failed to load"));
		};
		const handleLoad = () => {
			if (settled) return;
			const api = getProSealApi();
			if (!api) {
				fail();
				return;
			}
			settled = true;
			cleanup();
			resolve(api);
		};
		const handleError = () => fail();
		const timeoutId = window.setTimeout(fail, SCRIPT_TIMEOUT_MS);

		script.addEventListener("load", handleLoad, { once: true });
		script.addEventListener("error", handleError, { once: true });
		if (!existing) {
			script.id = SCRIPT_ID;
			script.async = true;
			script.src = PRO_SEAL_SCRIPT_URL;
			document.head.appendChild(script);
		}
	}).catch((error: unknown) => {
		scriptPromise = null;
		throw error;
	});

	return scriptPromise;
}

function hasRenderedWidget(element: HTMLElement): boolean {
	return Array.from(element.children).some(
		(child) => child.tagName !== "SCRIPT" && child.tagName !== "STYLE",
	);
}

export function ProvenExpertProSeal({ locale, fallbackLabel }: { locale: string; fallbackLabel: string }) {
	const language = resolveProSealLanguage(locale);
	const containerRef = useRef<HTMLDivElement>(null);
	const widgetRef = useRef<HTMLDivElement>(null);
	const [status, setStatus] = useState<ProSealStatus>("fallback");

	useEffect(() => {
		let disposed = false;
		let started = false;
		let intersectionObserver: IntersectionObserver | null = null;
		let widgetObserver: MutationObserver | null = null;
		let availabilityTimeoutId: number | null = null;
		const controller = new AbortController();

		const initialize = async () => {
			if (started) return;
			started = true;
			availabilityTimeoutId = window.setTimeout(() => controller.abort(), AVAILABILITY_TIMEOUT_MS);

			const available = await isProSealAvailable(controller.signal);
			if (availabilityTimeoutId !== null) window.clearTimeout(availabilityTimeoutId);
			if (disposed || !available) return;

			setStatus("loading");
			try {
				const api = await loadProSealScript();
				const widget = widgetRef.current;
				if (disposed || !widget) return;

				const markReady = () => {
					if (!disposed && hasRenderedWidget(widget)) setStatus("ready");
				};
				widgetObserver = new MutationObserver(markReady);
				widgetObserver.observe(widget, { childList: true, subtree: true });

				api.proSeal({
					widgetId: PRO_SEAL_WIDGET_ID,
					language,
					usePageLanguage: false,
					bannerColor: "#00793d",
					textColor: "#FFFFFF",
					showBackPage: true,
					showReviews: true,
					hideDate: true,
					hideName: false,
					googleStars: true,
					displayReviewerLastName: false,
					embeddedSelector: "#proSealWidget",
				});
				markReady();
			} catch {
				if (!disposed) setStatus("fallback");
			}
		};

		const container = containerRef.current;
		if (container && typeof IntersectionObserver !== "undefined") {
			intersectionObserver = new IntersectionObserver(
				(entries) => {
					if (!entries.some((entry) => entry.isIntersecting)) return;
					intersectionObserver?.disconnect();
					void initialize();
				},
				{ rootMargin: "300px" },
			);
			intersectionObserver.observe(container);
		} else {
			void initialize();
		}

		return () => {
			disposed = true;
			controller.abort();
			if (availabilityTimeoutId !== null) window.clearTimeout(availabilityTimeoutId);
			intersectionObserver?.disconnect();
			widgetObserver?.disconnect();
		};
	}, [language]);

	return (
		<div ref={containerRef} className="relative h-[260px] w-[150px]" data-proseal-state={status}>
			<a
				href={PRO_SEAL_PROFILE_URL}
				target="_blank"
				rel="noopener noreferrer"
				aria-hidden={status === "ready"}
				tabIndex={status === "ready" ? -1 : 0}
				className={`hover:border-primary/50 absolute inset-0 flex flex-col items-center justify-center rounded-xl border border-border bg-background px-4 text-center text-foreground shadow-sm transition-opacity focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
					status === "ready" ? "pointer-events-none opacity-0" : "opacity-100"
				}`}
			>
				<span className="text-primary" aria-hidden="true">
					★★★★★
				</span>
				<span className="mt-2 text-sm font-semibold">ProvenExpert</span>
				<span className="mt-1 text-xs leading-relaxed text-muted-foreground">{fallbackLabel}</span>
			</a>

			<div
				id="proSealWidget"
				ref={widgetRef}
				aria-hidden={status !== "ready"}
				className={`absolute inset-0 transition-opacity ${
					status === "ready" ? "opacity-100" : "pointer-events-none opacity-0"
				}`}
			/>
		</div>
	);
}
